import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const COPY_DEFAULTS: Record<string, string> = {
  "banner.desktop":
    "🏕️🌊 $199 CABANAS & $24.99 ALL-DAY TUBING THIS LABOR DAY WEEKEND - BOOK NOW! 🎉☀️",
  "banner.mobile":
    "🏕️🌊 $199 CABANAS & $24.99 ALL-DAY TUBING THIS LABOR DAY WEEKEND - BOOK NOW! 🎉☀️",
  "banner.href": "/book-now",
  "banner.enabled": "true",
  "banner.startAt": "",
  "banner.endAt": "",
  "banner.bg": "",
  "banner.fg": "",

  "popup.headline": "Planning a river day?",
  "popup.price": "Cabanas & all-day tubing from $24.99",
  "popup.subline": "Cabanas include entry for 8 and:",
  "popup.cta": "Book Now",
  "popup.accommodations": "cabana",
  "popup.cards": "",
  "popup.href": "/book-now",
  "popup.image": "cabana-riverside",
  "popup.images": "",
  "popup.delayMs": "10000",
  "popup.enabled": "false",
  "popup.startAt": "",
  "popup.endAt": "",
  "popup.accent": "",
  "popup.buttonFg": "",
};


export const COPY_EVENT = "site-copy-overrides-updated";

const cache = new Map<string, string>();

function emit() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(COPY_EVENT));
  }
}

let started = false;

function start() {
  if (started || typeof window === "undefined") return;
  started = true;

  void (async () => {
    const { data, error } = await supabase
      .from("site_copy_overrides")
      .select("key, value");
    if (!error && data) {
      for (const row of data) cache.set(row.key, row.value);
      emit();
    }
  })();

  supabase
    .channel("site-copy-overrides")
    .on(
      "postgres_changes",
      { event: "*", schema: "public", table: "site_copy_overrides" },
      (payload) => {
        const newRow = payload.new as { key?: string; value?: string } | null;
        const oldRow = payload.old as { key?: string } | null;
        if (payload.eventType === "DELETE") {
          if (oldRow?.key) cache.delete(oldRow.key);
        } else if (newRow?.key) {
          cache.set(newRow.key, newRow.value ?? "");
        }
        emit();
      },
    )
    .subscribe();
}

start();

export function getOverride(key: string): string {
  const v = cache.get(key);
  if (v !== undefined && v !== null) return v;
  return COPY_DEFAULTS[key] ?? "";
}

/** Raw override rows currently in cache (no defaults). */
export function getOverrideRows(): Map<string, string> {
  return new Map(cache);
}

/** Reads ?promo_preview=banner|popup from the current URL. */
export function promoPreview(): "banner" | "popup" | null {
  if (typeof window === "undefined") return null;
  try {
    const v = new URLSearchParams(window.location.search).get("promo_preview");
    return v === "banner" || v === "popup" ? v : null;
  } catch {
    return null;
  }
}

/** Accepts any CSS color string; ignores empty or overly long values. */
export function safeColor(value: string): string | undefined {
  const v = (value ?? "").trim();
  if (!v || v.length > 40) return undefined;
  return v;
}


export function useCopyOverride(key: string, fallback?: string): string {
  const [value, setValue] = useState(() => getOverride(key) || fallback || "");

  useEffect(() => {
    const update = () => setValue(getOverride(key) || fallback || "");
    update();
    window.addEventListener(COPY_EVENT, update);
    return () => window.removeEventListener(COPY_EVENT, update);
  }, [key, fallback]);

  return value;
}

function parseDate(raw: string): number | null {
  if (!raw || !raw.trim()) return null;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : null;
}

function computeActive(prefix: "banner" | "popup"): boolean {
  if (promoPreview() === prefix) return true;
  if (getOverride(`${prefix}.enabled`) === "false") return false;
  const now = Date.now();
  const start = parseDate(getOverride(`${prefix}.startAt`));
  if (start !== null && start > now) return false;
  const end = parseDate(getOverride(`${prefix}.endAt`));
  if (end !== null && end < now) return false;
  return true;
}

export function usePromoActive(prefix: "banner" | "popup"): boolean {
  const [active, setActive] = useState(() => computeActive(prefix));

  useEffect(() => {
    const update = () => setActive(computeActive(prefix));
    update();
    window.addEventListener(COPY_EVENT, update);
    const interval = window.setInterval(update, 60000);
    return () => {
      window.removeEventListener(COPY_EVENT, update);
      window.clearInterval(interval);
    };
  }, [prefix]);

  return active;
}
